import { useApolloClient } from '@apollo/client';
import { captureException as captureSentryException } from '@sentry/react';
import { useCallback } from 'react';

import {
  error as commonError,
  finleyAssert,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { PipelineSubmissionInputGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  CreatePipelineSubmissionDocumentGQLType,
  CreatePipelineSubmissionMutationGQLType,
  CreatePipelineSubmissionMutationVariablesGQLType,
} from 'packages/dataPipeline/requests/dataPipeline.gql.generated';
import { RDFV } from 'packages/dataPipeline/types/dataPipelineTypes';
import { postFileToPresignedUrl } from 'packages/files/ui/utils';

export type UploadCallback = (params: {
  loaded: number;
  total: number;
}) => void;

export type DirectUpload = ({
  file,
  onEventCallback,
}: {
  file: File;
  onEventCallback: UploadCallback;
}) => Promise<void>;

// fills the input with the file an earlier version of the date received
export type DirectKeep = (params: { fromFileVersion: number }) => Promise<void>;

export type PipelineSubmission = CreatePipelineSubmissionMutationGQLType['createPipelineSubmission'];

/**
 * Opens a pipeline submission of a reportingDate's inputs: `newRun` starts
 * `fileVersion`, which must be the next version (rejected when someone else just
 * started it); otherwise the inputs fill in the existing `fileVersion`.
 */
export const useCreatePipelineSubmission = (): ((
  variables: CreatePipelineSubmissionMutationVariablesGQLType,
) => Promise<PipelineSubmission>) => {
  const client = useApolloClient();
  return useCallback(
    async (
      variables: CreatePipelineSubmissionMutationVariablesGQLType,
    ): Promise<PipelineSubmission> => {
      const { data } = await client.mutate<
        CreatePipelineSubmissionMutationGQLType,
        CreatePipelineSubmissionMutationVariablesGQLType
      >({ mutation: CreatePipelineSubmissionDocumentGQLType, variables });
      finleyAssert(data, 'createPipelineSubmission returned no data.');
      return data.createPipelineSubmission;
    },
    [client],
  );
};

/**
 * Sends one input through a pipeline submission: a new file (`directUpload`) or the
 * file an earlier version received (`directKeep`). While `onRunStarted` is non-null
 * the version is the drawer's guess of the next one: the submission starts it, or is
 * rejected when someone else just did, so it never joins another's run. A null
 * `onRunStarted` means the run exists: the input only fills it in.
 */
export const useDirectUpload = ({
  implementationId,
  artifactSlug,
  fileExtension,
  rdfv,
  onRunStarted,
}: {
  implementationId: string;
  artifactSlug: string;
  // the input's configured file type; null for an input uploaded otherwise (cash balance)
  fileExtension: Util.SupportedFileExtension | null;
  rdfv: RDFV;
  onRunStarted: (() => void) | null;
}): { directUpload: DirectUpload; directKeep: DirectKeep } => {
  const { reportingDate, fileVersion } = rdfv;
  const createPipelineSubmission = useCreatePipelineSubmission();

  const openSubmission = useCallback(
    async (
      input: PipelineSubmissionInputGQLType,
    ): Promise<PipelineSubmission> => {
      const submission = await createPipelineSubmission({
        implementationId,
        reportingDate,
        fileVersion,
        newRun: onRunStarted !== null,
        inputs: [input],
      });
      onRunStarted?.();
      return submission;
    },
    [
      createPipelineSubmission,
      fileVersion,
      implementationId,
      onRunStarted,
      reportingDate,
    ],
  );

  const directUpload = useCallback(
    async ({
      file,
      onEventCallback,
    }: {
      file: File;
      onEventCallback: UploadCallback;
    }): Promise<void> => {
      finleyAssert(fileExtension, `${artifactSlug} is not uploaded as a file.`);
      const submission = await openSubmission({
        artifactSlug,
        uploadFileExtension: fileExtension,
      });
      const upload = submission.uploads.find(
        (candidate) => candidate.artifactSlug === artifactSlug,
      );
      finleyAssert(
        upload,
        `createPipelineSubmission: no upload for ${artifactSlug}`,
      );

      try {
        await postFileToPresignedUrl({
          url: upload.url,
          fields: upload.fields,
          file,
          progressEventCallback: ({ loaded, total }) => {
            onEventCallback({ loaded, total });
          },
        });
      } catch (error) {
        const outerError = new Error(
          `Failed to upload file to S3: ${commonError.getErrorMessage(
            error,
          )}. [implementationId:${implementationId}] [artifactSlug:${artifactSlug}] [reportingDate:${reportingDate}] [fileVersion:${
            submission.fileVersion
          }]`,
        );
        captureSentryException(outerError);
        throw outerError;
      }
    },
    [
      artifactSlug,
      fileExtension,
      implementationId,
      openSubmission,
      reportingDate,
    ],
  );

  // the server copies the kept file into the run: there is nothing to send
  const directKeep = useCallback(
    async ({ fromFileVersion }: { fromFileVersion: number }): Promise<void> => {
      await openSubmission({
        artifactSlug,
        keepFromFileVersion: fromFileVersion,
      });
    },
    [artifactSlug, openSubmission],
  );

  return {
    directUpload,
    directKeep,
  };
};
